import { useEffect, useState } from "react";
import { Checkin } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarCheck, Flame, Gift, Sparkles } from "lucide-react";

interface Status {
  enabled: boolean;
  today_signed: boolean;
  today_points: number;
  next_streak: number;
  current_streak: number;
  longest_streak: number;
  total_checkins: number;
  total_points: number;
  cycle_days: number;
  base_points: number;
  increment_per_day: number;
  max_daily_points: number;
  milestones: { day: number; bonus: number }[];
  recent: { date: string; points: number; streak: number }[];
}

export function CheckinPanel({ onSigned }: { onSigned?: () => void }) {
  const [s, setS] = useState<Status | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>("");

  const reload = () => Checkin.status().then(setS).catch(() => {});
  useEffect(() => { reload(); }, []);

  if (!s) return null;
  if (!s.enabled) return null;

  const sign = async () => {
    if (busy || s.today_signed) return;
    setBusy(true);
    setMsg("");
    try {
      const r = await Checkin.do();
      setMsg(`签到成功！+${r.points_award} 积分${r.bonus > 0 ? `（含里程碑奖励 +${r.bonus}）` : ""}`);
      reload();
      onSigned?.();
    } catch (e: any) {
      setMsg(e?.body?.message || "签到失败");
    } finally {
      setBusy(false);
    }
  };

  // 当前周期内的天数表（用于显示日历点）
  const cycle = Array.from({ length: s.cycle_days }, (_, i) => i + 1);
  const inCycleStreak = ((s.current_streak - 1 + (s.today_signed ? 0 : 1)) % s.cycle_days) + 1;
  const reachedDay = s.today_signed ? ((s.current_streak - 1) % s.cycle_days) + 1 : ((s.current_streak) % s.cycle_days);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarCheck className="h-4 w-4 text-emerald-500" /> 每日签到
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat icon={<Flame className="h-4 w-4 text-orange-500" />} label="连续签到" value={`${s.current_streak} 天`} />
          <Stat icon={<Sparkles className="h-4 w-4 text-amber-500" />} label="历史最长" value={`${s.longest_streak} 天`} />
          <Stat icon={<Gift className="h-4 w-4 text-rose-500" />} label="累计签到" value={`${s.total_checkins} 次`} />
          <Stat icon={<Sparkles className="h-4 w-4 text-emerald-500" />} label="累计积分" value={`${s.total_points}`} />
        </div>

        <div className="rounded-lg border p-4 flex flex-col sm:flex-row items-center gap-3">
          <div className="flex-1">
            <div className="text-xs text-muted-foreground">
              {s.today_signed ? "今日已签到" : `今日可获得`}
            </div>
            <div className="text-2xl font-bold flex items-baseline gap-1">
              +{s.today_points}
              <span className="text-sm font-normal text-muted-foreground">积分</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              连续签到第 {s.today_signed ? s.current_streak : s.next_streak} 天
            </div>
          </div>
          <Button size="lg" disabled={busy || s.today_signed} onClick={sign}>
            {s.today_signed ? "今日已签到" : busy ? "处理中…" : "立即签到"}
          </Button>
        </div>

        {msg && <div className="text-sm text-emerald-600">{msg}</div>}

        {/* 周期日历点 */}
        <div>
          <div className="text-xs text-muted-foreground mb-2">本周期（{s.cycle_days} 天）签到进度</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {cycle.map((day) => {
              const points = Math.min(s.base_points + s.increment_per_day * (day - 1), s.max_daily_points);
              const reached = day <= reachedDay;
              return (
                <div key={day}
                  className={"flex flex-col items-center justify-center rounded-md border w-12 h-14 text-xs " +
                    (reached ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-700" : "bg-muted/30")}>
                  <div className="font-semibold">第{day}天</div>
                  <div className="text-[10px] text-muted-foreground">+{points}</div>
                </div>
              );
            })}
          </div>
        </div>

        {s.milestones.length > 0 && (
          <div>
            <div className="text-xs text-muted-foreground mb-2">里程碑额外奖励</div>
            <div className="flex flex-wrap gap-2">
              {s.milestones.map((m) => (
                <div key={m.day} className="rounded-full border px-3 py-1 text-xs flex items-center gap-1.5">
                  <Gift className="h-3 w-3 text-rose-500" />
                  连续 {m.day} 天 +{m.bonus} 积分
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon}{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}
